import { useEffect, useState } from "react";
import { applyTheme, nextTheme, readTheme, resolveTheme } from "../lib/theme.js";

/* Light, dark, or whatever the operating system says.
 *
 * The board shipped with a light palette nobody could reach: the tokens
 * were there and the `[data-theme]` selectors were there, but the only way
 * to change the palette was to change the whole machine. This is the
 * control, and it is one button rather than three so the toolbar does not
 * grow — the label says which state it is in and what pressing it does,
 * because an icon on its own cannot. */

const WORD = {
  system: "follows your system",
  light: "light",
  dark: "dark",
};

export function ThemeToggle() {
  const [theme, setTheme] = useState(readTheme);
  const [shown, setShown] = useState(() => resolveTheme(readTheme()));

  useEffect(() => {
    applyTheme(theme);
    setShown(resolveTheme(theme));
    /* On "system" the palette moves when the machine does, so the icon has
       to follow without anyone pressing anything. The stylesheet switches
       either way — this only keeps the button from describing the palette
       it used to be. Visibility is checked as well as the media query,
       because the common case is a machine that turns dark at sunset while
       this tab sits in the background. */
    if (theme !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: light)");
    const sync = () => setShown(resolveTheme("system"));
    mq.addEventListener("change", sync);
    document.addEventListener("visibilitychange", sync);
    return () => {
      mq.removeEventListener("change", sync);
      document.removeEventListener("visibilitychange", sync);
    };
  }, [theme]);

  const next = nextTheme(theme);
  const state =
    theme === "system" ? WORD.system + ", " + shown + " right now" : WORD[theme];

  return (
    <button
      type="button"
      className="btn btn-sm themebtn"
      onClick={() => setTheme(next)}
      aria-label={"Theme: " + state + ". Press for " + WORD[next] + "."}
      title={"Theme: " + state + " — press for " + WORD[next]}
    >
      <ThemeIcon theme={theme} />
    </button>
  );
}

function ThemeIcon({ theme }) {
  if (theme === "system") {
    /* Half of each palette: the state that follows the machine should look
       like neither fixed choice. */
    return (
      <svg width="15" height="15" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
        <circle cx="8" cy="8" r="6.1" fill="none" stroke="currentColor" strokeWidth="1.4" />
        <path d="M8 1.9a6.1 6.1 0 0 0 0 12.2z" fill="currentColor" />
      </svg>
    );
  }

  if (theme === "light") {
    return (
      <svg width="15" height="15" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
        <circle cx="8" cy="8" r="3.1" fill="none" stroke="currentColor" strokeWidth="1.4" />
        <g stroke="currentColor" strokeWidth="1.4" strokeLinecap="round">
          <path d="M8 1v1.6M8 13.4V15M15 8h-1.6M2.6 8H1M12.9 3.1l-1.1 1.1M4.2 11.8l-1.1 1.1M12.9 12.9l-1.1-1.1M4.2 4.2L3.1 3.1" />
        </g>
      </svg>
    );
  }

  return (
    <svg width="15" height="15" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <path
        d="M13.2 9.9A5.6 5.6 0 0 1 6.1 2.8a5.7 5.7 0 1 0 7.1 7.1z"
        fill="currentColor"
      />
    </svg>
  );
}
